/**
 * Zod schema for "Add ServiceNow Connection" (Charter M3 migration), imported by
 * `ServiceNowConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VSN-*`
 * mirror the pre-migration hand-rolled `validate()` exactly, including the domain-substring check
 * that only fires once the URL is independently confirmed parseable.
 */
import { z } from 'zod';
import type { CreateServiceNowConnectionRequest, ServiceNowConnection } from '../types';
import { SERVICENOW_AUTH_TYPE_OPTIONS, isParseableUrl } from './connectionFormFields';

// reason: SERVICENOW_AUTH_TYPE_OPTIONS is a runtime-verified non-empty array of the exported
// union type; zod's z.enum() requires a literal non-empty tuple type.
const AUTH_TYPE_TUPLE = SERVICENOW_AUTH_TYPE_OPTIONS.map((o) => o.value) as [
  ServiceNowConnection['authType'],
  ...ServiceNowConnection['authType'][],
];

function isServiceNowDomain(value: string): boolean {
  return value.includes('service-now.com') || value.includes('servicenow.com');
}

export const ServiceNowConnectionSchema = z.object({
  // VSN-01
  name: z.string().trim().min(1, 'Connection name is required'),
  // VSN-02 (required) / VSN-03 (must be a parseable URL) / VSN-04 (should be a ServiceNow
  // instance URL — only evaluated once the URL is independently parseable, matching the
  // original's nested `try { new URL(...); if (!includes(...)) {...} } catch {...}`).
  instanceUrl: z
    .string()
    .trim()
    .min(1, 'Instance URL is required')
    .refine(isParseableUrl, { message: 'Please enter a valid URL' })
    .refine((v) => !isParseableUrl(v) || isServiceNowDomain(v), {
      message: 'URL should be a ServiceNow instance URL',
    }),
  authType: z.enum(AUTH_TYPE_TUPLE),
  // VSN-05
  username: z.string().trim().min(1, 'Username is required'),
  // VSN-06
  password: z.string().trim().min(1, 'Password is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type ServiceNowConnectionFormValues = z.input<typeof ServiceNowConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type ServiceNowConnectionFormOutput = z.output<typeof ServiceNowConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = ServiceNowConnectionFormOutput extends CreateServiceNowConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
